
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { usePageSEO } from '../../hooks/usePageSEO';
import { createBreadcrumbStructuredData, createArticleStructuredData } from '../../utils/structuredData';

export default function FxGuidePage() {
  usePageSEO({
    title: 'FX初心者ガイド｜始め方・基礎知識をわかりやすく解説',
    description: 'FX初心者向けに、FXの始め方、仕組み、リスク管理などをわかりやすく解説。DMM FXで安心してFXを始めるための完全ガイド。',
    keywords: 'FX,初心者,始め方,入門,ガイド,DMM FX',
    structuredData: [
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: 'FX初心者ガイド', url: '/fx-guide' },
      ]),
      createArticleStructuredData({
        headline: 'FX初心者ガイド',
        description: 'FX初心者向けに、FXの始め方、仕組み、リスク管理などをわかりやすく解説します。',
        author: 'DMM FX情報局',
        datePublished: '2024-01-15',
        dateModified: new Date().toISOString().split('T')[0],
        image: 'https://public.readdy.ai/ai/img_res/080a113ace87a70dc4a90544c96f62c2.jpg',
        url: '/fx-guide',
      }),
    ],
  });

  const articles = [
    {
      icon: 'ri-user-star-line',
      title: 'DMM FXは初心者でも大丈夫？',
      description: 'FX初心者がDMM FXで安心して始められる理由を徹底解説。メリット・デメリットも紹介。',
      url: '/beginner-guide',
    },
    {
      icon: 'ri-book-open-line',
      title: 'FXとは？超入門ガイド',
      description: 'FXの仕組み、レバレッジ、スプレッドなど基本から分かりやすく解説。',
      url: '/fx-basics',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'FXは危険？安全性を解説',
      description: '金融庁登録・信託保全など、FXの安全性について詳しく解説します。',
      url: '/safety',
    },
    {
      icon: 'ri-file-list-3-line',
      title: '口座開設のやり方',
      description: 'スマホで最短5分。画像付きで口座開設の手順を詳しく解説。',
      url: '/account-opening',
    },
    {
      icon: 'ri-smartphone-line',
      title: 'アプリの使い方',
      description: 'DMM FXスマホアプリの操作方法を画像付きで解説。',
      url: '/app-guide',
    },
    {
      icon: 'ri-checkbox-circle-line',
      title: 'FX口座の選び方',
      description: '初心者がFX口座を選ぶ際のポイントを解説。',
      url: '/how-to-choose',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'FX初心者ガイド', url: '/fx-guide' },
          ]} />

          <div className="mb-10">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">FX初心者ガイド</h1>
            <p className="text-gray-600 leading-relaxed">
              FXをこれから始める方向けに、仕組み・始め方・注意点をわかりやすく解説しています。
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article, index) => (
              <Link
                key={index}
                to={article.url}
                className="group bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-teal-300 transition-all cursor-pointer"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className={`${article.icon} text-white text-xl`}></i>
                </div>
                <h2 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                  {article.title}
                </h2>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">{article.description}</p>
                <div className="flex items-center text-teal-600 text-sm font-semibold">
                  <span>詳しく見る</span>
                  <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform"></i>
                </div>
              </Link>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 text-center bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8">
            <p className="text-gray-700 mb-4">まずは無料で口座開設してみましょう</p>
            <a 
              href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
              rel="nofollow"
              className="inline-block bg-gradient-to-b from-orange-500 to-orange-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:scale-105 transition-all whitespace-nowrap cursor-pointer shadow-lg"
            >
              無料でDMM FX口座を開設する
            </a>
            <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
